import * as React from "react";

import type { VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import {
  AlertCircle,
  AlertTriangle,
  CircleCheckBig,
  Info,
  Terminal,
} from "lucide-react";

import { cn } from "../../utils";

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>;

const alertVariants = cva(
  "af-relative af-w-full af-rounded-lg af-border af-p-3",
  {
    variants: {
      variant: {
        default: "af-border-border af-bg-background af-text-foreground",
        destructive:
          "af-border-destructive af-text-destructive [&>svg]:af-text-destructive",
        info: "af-border-primary af-bg-interface-accent/30 af-text-primary [&>svg]:af-text-primary",
        warning: "af-border-warning af-text-warning [&>svg]:af-text-warning",
        success:
          "af-border-success-accent af-bg-success/40 af-text-success-foreground [&>svg]:af-text-success-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const defaultIcons: Record<AlertVariant, LucideIcon> = {
  default: Terminal,
  destructive: AlertCircle,
  info: Info,
  warning: AlertTriangle,
  success: CircleCheckBig,
};

const AlertContext = React.createContext<AlertVariant>("default");

interface AlertProps
  extends React.ComponentProps<"div">, VariantProps<typeof alertVariants> {
  variant?: AlertVariant;
}

function Alert({
  className,
  children,
  variant = "default",
  ...props
}: AlertProps) {
  return (
    <AlertContext value={variant}>
      <div
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        <div className="af-flex af-flex-col af-gap-0.5 [&:has(>svg)]:af-pl-6">
          {children}
        </div>
      </div>
    </AlertContext>
  );
}
Alert.displayName = "Alert";

interface AlertIconProps extends React.ComponentProps<"svg"> {
  asChild?: boolean;
}

const AlertIcon = ({ className, children, asChild }: AlertIconProps) => {
  const variant = React.useContext(AlertContext);
  const Comp = asChild ? Slot : defaultIcons[variant];

  return (
    <Comp
      className={cn(
        "af-absolute af-left-3 af-top-3.5 af-h-4 af-w-4",
        className,
      )}
    >
      {children}
    </Comp>
  );
};
AlertIcon.displayName = "AlertIcon";

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <h5
      className={cn("af-min-h-4 af-text-sm af-font-medium", className)}
      {...props}
    />
  );
}
AlertTitle.displayName = "AlertTitle";

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("af-text-xs [&_p]:af-leading-relaxed", className)}
      {...props}
    />
  );
}
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertIcon, AlertTitle, AlertDescription };
